/* eslint-disable no-undef */
import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import {
    makeStyles, Grid, Button,
    Switch, Tooltip,
} from '@material-ui/core';
import { cloneDeep } from 'lodash';
import { Form } from 'react-bootstrap';
import { FetchPolicy, ALL_LOTS } from 'utils/enum/Core';
import { useQuery, useMutation, useApolloClient } from '@apollo/client';
import ImagesUtils from 'utils/ImagesUtils';
import ModalUtils from 'utils/ModalUtils';
import StringUtils from 'lib/StringUtils';
import LotsCategory from 'utils/enum/LotsCategory';
import { modules } from 'utils/enum/modules';
import { BUCKET_SOURCE } from 'utils/enum/General';
import { InventoryCategory, InventorySection } from 'utils/enum/InventoryEnum';
import CompanyQuery from 'services/graphQL/query/core/CompanyQuery';
import CompanyMutation from 'services/graphQL/mutate/CompanyMutation';
import GeneralQuery from 'services/graphQL/query/GeneralQuery';
import GeneralMutation from 'services/graphQL/mutate/GeneralMutation';
import ButtonStyles from 'styles/theme/Button';
import LotQuery from 'services/graphQL/query/LotQuery';
import InventoryMutation from 'services/graphQL/mutate/InventoryMutation';
import If from 'components/widgets/conditional/If';
import ConfirmDialog from 'components/widgets/modal/ConfirmDialog';
import VirtualTable from 'components/widgets/VirtualTable';
import InputNumber from 'components/widgets/InputNumber';

// Icons
import DeleteOutlineOutlinedIcon from '@material-ui/icons/DeleteOutlineOutlined';

const buttonStyles = makeStyles((theme) => ButtonStyles.getStyle(theme));
const useStyles = makeStyles((theme) => ({
    box: {
        marginTop: '10px',
        paddingTop: '10px',
        paddingBottom: '10px',
        '& > div > button': {
            marginRight: '10px',
        },
    },
    input: {
        fontSize: '14px',
    },
    title: {
        borderTop: `solid 1px ${theme.palette.border.mercury}`,
        fontSize: '12px',
        marginLeft: '15px',
        marginTop: '5px',
        paddingTop: '10px',
    },
    addContainer: {
        marginTop: '10px',
        paddingLeft: '15px',
        paddingRight: '15px',
        display: 'flex',
        '& > input': {
            width: '250px',
        },
        '& > button': {
            marginLeft: '5px',
        },
    },
    deleteButton: {
        minWidth: '30px',
        '& .MuiButton-startIcon': {
            marginRight: 0,
        },
    },
    tableContainer: {
        marginTop: '15px',
        paddingLeft: '15px',
        paddingRight: '15px',
        height: '300px',
        overflow: 'hidden',
        [theme.breakpoints.down('md')]: {
            overflowX: 'auto',
            overflowY: 'hidden',
            '& > div': {
                minWidth: '600px',
            },
        },
    },
    tableHeader: {
        textAlign: 'left',
        color: theme.palette.text.waterloo,
        borderRight: `1px solid ${theme.palette.border.ghost}`,
        height: '100%',
        alignItems: 'center',
    },
    uploader: {
        display: 'none',
    },
    warning: {
        fontSize: '12px',
        fontWeight: '300',
        marginLeft: '15px',
        marginTop: '2px',
    },
    columnCell: {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    toggle: {
        display: 'flex',
        flexDirection: 'row',
        paddingLeft: '15px',
        paddingRight: '15px',
        alignItems: 'center',
        fontSize: '12px',
        marginBottom: '10px',
    },
    interval: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        marginRight: '15px',
        marginTop: '30px',
        fontSize: '13px',
        '& > input': {
            width: '50px',
            marginLeft: '5px',
            marginRight: '5px',
        },
    },
}));

const AdvertisingSettings = ({ canWrite }) => {
    const client = useApolloClient();
    const uploader = useRef(null);
    const resourceUploader = useRef(null);
    const classes = { ...useStyles(), ...buttonStyles() };
    const [state, setState] = useState({
        watermarks: [],
        resources: [],
        currentWatermark: null,
        isDeletePromptVisible: false,
        isDeletingWatermark: false,
        isUploadingWatermark: false,
        watermarkName: null,
        enableDefaultAds: false,
        resourceName: null,
        currentResource: null,
        isUploadingResource: false,
        isDeletingResource: false,
        insertResourceAt: null,
    });

    const {
        data: watermarksData,
        loading: watermarksLoading,
        error: watermarksError,
    } = useQuery(CompanyQuery.PULL_AVAILABLE_WATERMARKS, {
        fetchPolicy: FetchPolicy.NETWORK_ONLY,
    });

    const {
        data: resourcesData,
        loading: resourcesLoading,
        error: resourcesError,
        refetch: refetchResources,
    } = useQuery(GeneralQuery.PULL_COMMON_RESOURCES, {
        variables: {
            module: modules.INVENTORY,
            section: InventorySection.ADVERTISING,
        },
        notifyOnNetworkStatusChange: true,
        fetchPolicy: FetchPolicy.NETWORK_ONLY,
    });

    const { PLACE_ONLINE_ADS_DEFAULT, ADVERTISING_IMAGE_INSERT_AT } = InventoryCategory;
    const {
        data: settingsData,
        loading: settingsLoading,
        error: settingsError,
    } = useQuery(LotQuery.GET_SETTINGS, {
        variables: {
            category: LotsCategory.INVENTORY,
            lotName: ALL_LOTS,
            key: [PLACE_ONLINE_ADS_DEFAULT, ADVERTISING_IMAGE_INSERT_AT],
        },
        fetchPolicy: FetchPolicy.NETWORK_ONLY,
    });

    const [saveInventorySettings, { loading: savingSettings }] = useMutation(InventoryMutation.SAVE_INVENTORY_SETTINGS, {
        onError: (errorMessage) => {
            ModalUtils.errorMessage([errorMessage]);
        },
    });

    useEffect(() => {
        if (watermarksError) {
            ModalUtils.errorMessage(watermarksError?.graphQLErrors);
            return;
        }

        if (!watermarksLoading) {
            const watermarks = watermarksData?.pullAvailableWatermarks;
            if (watermarks) {
                setState((prevState) => ({
                    ...prevState,
                    watermarks: watermarks.slice().sort((a, b) => a.name.localeCompare(b.name)),
                }));
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [watermarksLoading, watermarksError]);

    useEffect(() => {
        if (resourcesError) {
            ModalUtils.errorMessage(resourcesError?.graphQLErrors);
            return;
        }

        if (!resourcesLoading) {
            const resources = resourcesData?.pullCommonResources;
            if (resources) {
                setState((prevState) => ({
                    ...prevState,
                    resources: resources.slice().sort((a, b) => a.resourceName.localeCompare(b.resourceName)),
                }));
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resourcesLoading, resourcesError]);

    useEffect(() => {
        if (settingsError) {
            ModalUtils.errorMessage(settingsError?.graphQLErrors);
            return;
        }

        if (!settingsLoading) {
            const settings = settingsData?.getSettings;
            if (settings) {
                const defaultInsertPosition = 3;
                const savedValue = settings.find((setting) => setting.key === PLACE_ONLINE_ADS_DEFAULT)?.value ?? 'false';
                const insertAt = settings.find((setting) => setting.key === ADVERTISING_IMAGE_INSERT_AT)?.value ?? defaultInsertPosition;
                setState((prevState) => ({
                    ...prevState,
                    enableDefaultAds: savedValue === 'true',
                    insertResourceAt: Number(insertAt),
                }));
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [settingsLoading, settingsError]);

    const toggleDeletePrompt = (entityId = null, isResources) => {
        setState((prevState) => ({
            ...prevState,
            isDeletePromptVisible: !state.isDeletePromptVisible,
            ...(entityId ? {
                ...(isResources ? {
                    currentResource: entityId,
                } : {}),
                ...(!isResources ? {
                    currentWatermark: entityId,
                } : {}),
            } : {
                currentResource: null,
                currentWatermark: null,
            }),
        }));
    };

    const removeResource = async () => {
        try {
            setState((prevState) => ({
                ...prevState,
                isDeletingResource: true,
            }));

            const { data } = await client.mutate({
                mutation: GeneralMutation.DELETE_COMMON_RESOURCE,
                variables: {
                    commonResourceId: state.currentResource,
                },
                fetchPolicy: FetchPolicy.NO_CACHE,
            });

            if (data) {
                ModalUtils.successMessage(null, 'Resource removed successfully');
                setState((prevState) => ({
                    ...prevState,
                    isDeletingResource: false,
                    isDeletePromptVisible: false,
                    currentResource: null,
                }));

                refetchResources();
            }
        } catch (error) {
            ModalUtils.errorMessage(null, error.message);
        }
    };

    const removeWatermark = async () => {
        try {
            setState((prevState) => ({
                ...prevState,
                isDeletingWatermark: true,
            }));

            const { data } = await client.mutate({
                mutation: CompanyMutation.REMOVE_WATERMARK,
                variables: {
                    companyWatermarkId: state.currentWatermark,
                },
                fetchPolicy: FetchPolicy.NO_CACHE,
            });

            if (data) {
                ModalUtils.successMessage(null, 'Watermark removed successfully');

                let clone = cloneDeep(state.watermarks);
                clone = clone.filter((element) => element.companyWatermarkId !== state.currentWatermark);
                setState((prevState) => ({
                    ...prevState,
                    isDeletingWatermark: false,
                    isDeletePromptVisible: false,
                    currentWatermark: null,
                    watermarks: clone,
                }));
            }
        } catch (error) {
            ModalUtils.errorMessage(null, error.message);
        }
    };

    const onChange = (name, value) => {
        if (name === 'insertResourceAt' && Number(value) > 0) {
            saveInventorySettings({
                variables: {
                    input: [
                        {
                            value: String(value),
                            key: ADVERTISING_IMAGE_INSERT_AT,
                            lotName: ALL_LOTS,
                        },
                    ],
                },
            });

            setState((prevState) => ({
                ...prevState,
                [name]: value,
            }));

            return;
        }

        if (name === 'enableDefaultAds') {
            saveInventorySettings({
                variables: {
                    input: [
                        {
                            value: value ? 'true' : 'false',
                            key: PLACE_ONLINE_ADS_DEFAULT,
                            lotName: ALL_LOTS,
                        },
                    ],
                },
            });
        }

        setState((prevState) => ({
            ...prevState,
            [name]: value,
        }));
    };

    const openFileDialog = (isResource = false) => {
        if (isResource) {
            const { current } = resourceUploader;
            if (current) current.click();

            return;
        }

        const { current } = uploader;
        if (current) current.click();
    };

    const uploadResource = async ({ target }) => {
        const file = target.files[0];
        const { size, name } = file;
        const ext = name.substr(name.lastIndexOf('.'))?.toLowerCase();

        // eslint-disable-next-line no-param-reassign
        target.value = null;

        const maxSizeAllowed = ImagesUtils.MAX_SIZES_WITH_ERROR.IMAGES.SIZE;
        if (size > maxSizeAllowed) {
            ModalUtils.errorMessage(null, ImagesUtils.MAX_SIZES_WITH_ERROR.IMAGES.ERROR);
            return;
        }

        if (!['.png', '.jpeg', '.jpg'].includes(ext)) {
            ModalUtils.errorMessage(null, 'Only png, jpeg and jpg images are supported');
            return;
        }

        setState((prevState) => ({
            ...prevState,
            isUploadingResource: true,
        }));

        try {
            const { data } = await client.mutate({
                mutation: GeneralMutation.UPLOAD_COMMON_RESOURCE,
                variables: {
                    module: modules.INVENTORY,
                    section: InventorySection.ADVERTISING,
                    bucket: BUCKET_SOURCE.IMAGES,
                    resourceName: state.resourceName,
                    file,
                },
                fetchPolicy: FetchPolicy.NO_CACHE,
            });

            const response = data?.uploadCommonResource;
            if (response) {
                setState((prevState) => ({
                    ...prevState,
                    resourceName: null,
                    currentResource: null,
                    isUploadingResource: false,
                }));

                refetchResources();
            }
        } catch (error) {
            setState((prevState) => ({
                ...prevState,
                isUploadingResource: false,
            }));

            ModalUtils.errorMessage(null, error.message);
        }
    };

    const uploadWatermark = async ({ target }) => {
        const file = target.files[0];
        const { size, name } = file;
        const ext = name.substr(name.lastIndexOf('.'))?.toLowerCase();

        // eslint-disable-next-line no-param-reassign
        target.value = null;

        const maxSizeAllowed = 307200; // 300KB in bytes
        if (size > maxSizeAllowed) {
            ModalUtils.errorMessage(null, 'Max file allowed is 300KB.');
            return;
        }

        if (ext !== '.png') {
            ModalUtils.errorMessage(null, 'Only png images are supported');
            return;
        }

        setState((prevState) => ({
            ...prevState,
            isUploadingWatermark: true,
        }));

        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = (readerEvent) => {
            const image = new Image();
            image.src = readerEvent.target.result;
            image.onload = async () => {
                const { width } = image;
                if (width > 1920) {
                    ModalUtils.errorMessage(null, 'Maximum width allowed: 1920px');
                    return;
                }

                try {
                    const { data } = await client.mutate({
                        mutation: CompanyMutation.ADD_WATERMARK,
                        variables: {
                            name: state.watermarkName,
                            file,
                        },
                        fetchPolicy: FetchPolicy.NO_CACHE,
                    });

                    const response = data?.addWatermark;
                    if (response) {
                        const { companyWatermarkId, url } = response;
                        const clone = cloneDeep(state.watermarks);
                        clone.unshift({
                            companyWatermarkId,
                            name: state.watermarkName,
                            url,
                        });

                        setState((prevState) => ({
                            ...prevState,
                            watermarkName: null,
                            currentWatermark: null,
                            isUploadingWatermark: false,
                            watermarks: clone.slice().sort((a, b) => a.name.localeCompare(b.name)),
                        }));
                    }
                } catch (error) {
                    setState((prevState) => ({
                        ...prevState,
                        isUploadingWatermark: false,
                    }));

                    ModalUtils.errorMessage(null, error.message);
                }
            };
        };
    };

    const getColumns = (isResources = false) => [
        {
            headerClassName: classes.tableHeader,
            label: 'Name',
            dataKey: 'name',
            width: 300,
            cellRenderer: (cell) => {
                const { rowData: record } = cell;

                return (
                    <span
                        tooltip={isResources ? record.resourceName : record.name}
                        className={classes.columnCell}
                    >
                        {isResources ? record.resourceName : record.name}

                    </span>
                );
            },
        },
        {
            headerClassName: classes.tableHeader,
            label: 'URL',
            dataKey: 'url',
            width: 500,
            cellRenderer: (cell) => {
                const { rowData: record } = cell;

                return (
                    <span>{isResources ? record.resourceURL : record.url}</span>
                );
            },
        },
        {
            headerClassName: classes.tableHeader,
            label: 'Action',
            dataKey: 'action',
            width: 80,
            cellRenderer: (cell) => {
                const {
                    rowData: {
                        companyWatermarkId,
                        commonResourceId,
                    },
                } = cell;

                return (
                    <Button
                        disabled={state.isUploadingResource}
                        className={clsx(classes.containedError, classes.deleteButton)}
                        size="small"
                        startIcon={<DeleteOutlineOutlinedIcon />}
                        onClick={() => toggleDeletePrompt(isResources ? commonResourceId : companyWatermarkId, isResources)}
                    />
                );
            },
        },
    ];

    return (
        <>
            <Grid container className={classes.box}>
                <Grid className={classes.toggle} item xs={12}>
                    <div>
                        Place online ads by default
                    </div>
                    <div>
                        <Switch
                            disabled={savingSettings}
                            checked={state.enableDefaultAds}
                            onChange={(event) => onChange('enableDefaultAds', event.target.checked)}
                            inputProps={{ 'aria-label': 'primary checkbox' }}
                        />
                    </div>
                </Grid>
                <Grid item xs={12}>
                    <div className={classes.title}>
                        Watermarks
                    </div>
                </Grid>
                <Grid item xs={12}>
                    <div className={classes.addContainer}>
                        <Form.Control
                            placeholder='Name the image then click "Add"'
                            maxLength={60}
                            disabled={!canWrite}
                            className={classes.input}
                            type="text"
                            name="watermarkName"
                            value={state.watermarkName || ''}
                            onChange={({ target: { name, value } }) => onChange(name, value)}
                        />
                        <If condition={canWrite}>
                            <input
                                ref={uploader}
                                className={classes.uploader}
                                type="file"
                                onChange={uploadWatermark}
                            />
                            <Button
                                disabled={StringUtils.isEmpty(state.watermarkName) || state.isUploadingWatermark}
                                className={classes.containedSecondaryInfo}
                                size="small"
                                onClick={() => openFileDialog()}
                            >
                                Add
                            </Button>
                        </If>
                    </div>
                    <div className={classes.warning}>
                        PNG only, maximum width allowed: 1920px
                    </div>
                </Grid>
                <Grid item xs={12}>
                    <div className={clsx(classes.tableContainer, 'virtual-table-base')}>
                        <VirtualTable
                            loading={watermarksLoading}
                            rowHeight={45}
                            totalRecords={state.watermarks.length}
                            data={state.watermarks}
                            columns={getColumns()}
                        />
                    </div>
                </Grid>
                <Grid item xs={12}>
                    <div className={classes.title}>
                        <Tooltip title="These marketing images will be inserted in between the vehicle images" placement="top">
                            <span>Common Images</span>
                        </Tooltip>
                    </div>
                </Grid>
                <Grid item md={6} xs={12}>
                    <div className={classes.addContainer}>
                        <Form.Control
                            placeholder='Name the image then click "Add"'
                            maxLength={100}
                            disabled={!canWrite}
                            className={classes.input}
                            type="text"
                            name="resourceName"
                            value={state.resourceName || ''}
                            onChange={({ target: { name, value } }) => onChange(name, value)}
                        />
                        <If condition={canWrite}>
                            <input
                                ref={resourceUploader}
                                className={classes.uploader}
                                type="file"
                                onChange={uploadResource}
                            />
                            <Button
                                disabled={StringUtils.isEmpty(state.resourceName) || state.isUploadingResource}
                                className={classes.containedSecondaryInfo}
                                size="small"
                                onClick={() => openFileDialog(true)}
                            >
                                Add
                            </Button>
                        </If>
                    </div>
                    <div className={classes.warning}>
                        JPEG, JPG, PNG only
                    </div>
                </Grid>
                <Grid item md={6} xs={12}>
                    <div className={classes.interval}>
                        <span>Insert an image after every</span>
                        <InputNumber
                            size="sm"
                            min={1}
                            value={state.insertResourceAt ?? 3}
                            allowNegative={false}
                            decimalScale={0}
                            onChange={(value) => onChange('insertResourceAt', value)}
                        />
                        <span>vehicle images.</span>
                    </div>
                </Grid>
                <Grid item xs={12}>
                    <div className={clsx(classes.tableContainer, 'virtual-table-base')}>
                        <VirtualTable
                            loading={resourcesLoading}
                            rowHeight={45}
                            totalRecords={state.resources.length}
                            data={state.resources}
                            columns={getColumns(true)}
                        />
                    </div>
                </Grid>
            </Grid>
            <ConfirmDialog
                title="Attention!"
                description="Do you want to remove this image?"
                open={state.isDeletePromptVisible}
                variant="outlined"
                titlePrimary="Yes"
                titleSecondary="Cancel"
                onClose={() => toggleDeletePrompt()}
                onClickSecondary={() => toggleDeletePrompt()}
                onClickPrimary={() => (state.currentResource ? removeResource() : removeWatermark())}
                disablePrimaryButton={state.isDeletingWatermark}
                disableSecondaryButton={state.isDeletingWatermark}
            />
        </>
    );
};

AdvertisingSettings.propTypes = {
    canWrite: PropTypes.bool.isRequired,
};

export default AdvertisingSettings;
